import { Component, Input, EventEmitter, Output, ChangeDetectionStrategy, OnInit } from '@angular/core';
import * as moment from 'moment-mini';
import { ScheduleFilter, DayOfMonth, ScheduledResource, DateRange, SchViews, ScheduleItemTypeEnum, ScheduleItemStatusEnum, ViewType, GanttDateClick } from './aims-scheduler.classes';
import { PermissionsService } from '../../../../../src/app/services/permissions/permissions.service';
import { TransferHttpService } from '../../../../../src/app/services/transfer-http/transfer-http.service';
import { Observable, of } from 'rxjs';
import { HrEmployees, SchTravelRequestLegs, SchFirearms } from '../../../../../src/models';
import { ScheduleItem } from '../schCommon';


@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'aims-scheduler-gantt',
  styleUrls: ['./aims-scheduler.component.css'],
  templateUrl: './aims-scheduler-gantt.component.html',
  exportAs: 'schedulerGanttView'
})
export class AimsSchedulerGanttComponent implements OnInit {

  constructor(public permissions: PermissionsService, private transferHttp: TransferHttpService) {
  }

  @Input() filter: ScheduleFilter;
  @Input() peopleScheduled: Observable<GanttLine[]> = of([]);
  @Output() scheduleItemSelected = new EventEmitter<ScheduleItem>();
  @Output() nameSelected = new EventEmitter<string>();
  @Output() ganttDateClick = new EventEmitter<GanttDateClick>();

  schViews = SchViews;
  m = moment;
  siStatus = ScheduleItemStatusEnum;

  vt = ViewType;
  daysToDisplay: Array<DayOfMonth> = [];


  sites = this.transferHttp.getCache('sitesDropdown');
  emps = this.transferHttp.getCache<Array<HrEmployees>>('employeeDropdown');

  traveler: HrEmployees = new HrEmployees();

  hoursInDay = ['12am', '1am', '2am', '3am', '4am', '5am', '6am', '7am', '8am', '9am', '10am', '11am', '12pm', '1pm', '2pm', '3pm', '4pm', '5pm', '6pm', '7pm', '8pm', '9pm', '10pm', '11pm'];

  showSpinner: boolean = false;
  spinnerText: string = "Loading...";

  //***************************
  // Widget Actions
  //***************************

  genDr(vt: ViewType, day: moment.Moment): GanttDateClick {
    return { dateRange: new DateRange(vt, day), view: vt };
  }

  showSchedule(person: GanttLine, schedule: GanttSchedule) {

    //This is to trigger the updating display
    schedule.siteCode = '';

    let newSchedule = Object.assign({}, schedule);
    let isEmp = this.emps.filter(e => e.employeeId == person.id && e.fullName == person.name).length > 0;
    let isFillGap = newSchedule.siteCode == "" && newSchedule.id == 0;

    if (isEmp) {
      newSchedule.employeeId = person.id;
    }
    else {
      newSchedule.resourceId = person.id;
    }

    if (isFillGap) {

      if (isEmp) {
        this.transferHttp.get('Sch/GetTravelerDetails?travelerId=' + newSchedule.employeeId).subscribe(traveler => {
          this.traveler = traveler;
          newSchedule.scheduleItemTypeId = person.schedule.filter(ps => ps.employeeId != null).map(ps => ps.scheduleItemTypeId)[0];
          newSchedule.employeeId = this.traveler.employeeId;
          newSchedule.siteId = this.traveler.defaultSiteId;
          if (newSchedule.scheduleItemTypeId == ScheduleItemTypeEnum.overhire) {
            newSchedule.overhireJtId = person.schedule.filter(ps => ps.employeeId != null).map(ps => ps.jobTitleId)[0];
          }
          this.scheduleItemSelected.emit(new ScheduleItem(newSchedule));

        });
      }
      else {
        newSchedule.scheduleItemTypeId = person.schedule.filter(ps => ps.resourceId != null).map(ps => ps.scheduleItemTypeId)[0];
        this.scheduleItemSelected.emit(new ScheduleItem(newSchedule));
      }
    }
    else {
      //Existing record, go get full details from DB
      if (newSchedule.id !== 0){
        this.showSpinner = true;
        this.transferHttp.getSingle(`Sch/GetScheduleItem?siId=${newSchedule.id}`).subscribe(s => {
          this.scheduleItemSelected.emit(new ScheduleItem(s));
          this.showSpinner = false;
        })
      }
    }
  }

  trackByPerson(index, person: GanttLine) {
    if (!person) return null;
    return person.id;
  }

  trackBySch(index, sch: GanttSchedule) {
    if (!sch) return null;
    return sch.id;
  }


  ngOnInit() {
  }

}



export class GanttLine {
  constructor(id: number, resourceName: string, jobTitleId: number, approved: boolean, scheduledResources: Array<ScheduledResource>, firstMoment: moment.Moment, lastMoment: moment.Moment) {
    this.id = id;
    this.name = resourceName;
    this.jobTitleId = jobTitleId;
    this.approved = approved;
    this.schedule = [];

    this._firstMoment = firstMoment;
    this._lastMoment = lastMoment;

    this.addToSchedule(scheduledResources);

  }

  private _firstMoment: moment.Moment;
  private _lastMoment: moment.Moment;

  id: number;
  name: string;
  jobTitleId: number;
  approved: boolean;
  schedule: Array<GanttSchedule>;

  public addToSchedule(scheduledResources: Array<ScheduledResource>) {
    //Create blocks and fill in with fill gaps
    let currentTime: moment.Moment = this._firstMoment;
    const totalMilliseconds: number = this._lastMoment.diff(this._firstMoment);  //Needed to calculate ratio for percent width

    for (let i = 0; i <= scheduledResources.length; i++) {
      const sch = (i + 1 > scheduledResources.length) ? null : scheduledResources[i];
      let schStartTime = (sch) ? moment(sch.start) : null;
      let schEndTime = (sch) ? moment(sch.end) : null;

      //Push into schedule if current, else create fill gap block
      if (schStartTime !== null && moment(schStartTime).add(-1, 'minute').isSameOrBefore(currentTime, 'minute')) {
        currentTime = this.insertScheduleItem(schStartTime, schEndTime, this._firstMoment, this._lastMoment, sch, totalMilliseconds);
      }
      else {
        //Caclulate width
        const start = (currentTime.isSameOrBefore(this._firstMoment)) ? this._firstMoment //Gantt line starts with Fill Gap
          : currentTime; //Fill gap after Gantt line

        if (start.isBefore(this._lastMoment, 'minute')) {
          const end = (sch !== null) ? moment(sch.start) //if additional scheduleitems exist, set end of fill gap to start of next schedule item
            : this._lastMoment;  //If no more schedule items exist set to end of display period
          const ratio = this.calcRatio(start, end, totalMilliseconds);

          this.schedule.push(new GanttSchedule(new ScheduledResource(start.toDate(), end.toDate()), ratio));
          currentTime = (sch !== null) ? this.insertScheduleItem(schStartTime, schEndTime, this._firstMoment, this._lastMoment, sch, totalMilliseconds) : this._lastMoment;
        }
      }

    }
  }

  //Need to truncate ratios because javascript sucks at math
  private truncate(v, p) {
    var s = Math.pow(10, p || 0);
    return Math.trunc(s * v) / s;
  }

  private calcRatio(start: moment.Moment, end: moment.Moment, totalMilliseconds: number) {
    const milliseconds = end.diff(start);
    const ratio = (milliseconds / totalMilliseconds) * 100;
    return ratio;
  }

  private insertScheduleItem(schStartTime: moment.Moment, schEndTime: moment.Moment, firstMoment: moment.Moment, lastMoment: moment.Moment, sch: ScheduledResource, totalMilliseconds: number): moment.Moment {
    //Caclulate width
    const start = (schStartTime.isSameOrBefore(firstMoment)) ? firstMoment : moment(sch.start);
    const end = (schEndTime.isSameOrAfter(lastMoment)) ? lastMoment : schEndTime;
    const ratio = this.calcRatio(start, end, totalMilliseconds);

    this.schedule.push(new GanttSchedule(sch, ratio));
    return moment(schEndTime);
  }
}

export class GanttSchedule {

  constructor(scheduleItem: any, width: number) {
    this.id = scheduleItem.id;
    this.start = moment(scheduleItem.start);
    this.end = moment(scheduleItem.end);
    this.company = scheduleItem.company;
    this.name = scheduleItem.name;
    this.jobTitleId = scheduleItem.jobTitleId;
    this.jobTitle = scheduleItem.jobTitle;
    this.siteId = scheduleItem.siteId;
    this.siteName = scheduleItem.siteName;
    this.siteCode = scheduleItem.siteCode;
    this.resourceId = scheduleItem.resourceId;
    this.employeeId = scheduleItem.employeeId;
    this.reliefForId = scheduleItem.reliefForId;
    this.reliefForName = scheduleItem.reliefForName;
    this.overhireJtId = scheduleItem.overhireJtId;
    this.overhireJt = scheduleItem.overhireJt;
    this.resourceDescription = scheduleItem.resourceDescription;
    this.scheduleItemTypeId = scheduleItem.scheduleItemTypeId;
    this.requiresApproval = scheduleItem.requiresApproval;
    this.isEmployeeSiType = scheduleItem.isEmployeeSiType;
    this.color = scheduleItem.color;
    this.isVip = scheduleItem.isVip;
    this.isCarrier = scheduleItem.isCarrier;
    this.icon = scheduleItem.icon;
    this.gender = scheduleItem.gender;
    this.travelArrangeReq = scheduleItem.travelArrangeReq;
    this.meals = scheduleItem.meals;
    this.govBilled = scheduleItem.govBilled;
    this.companyBilled = scheduleItem.companyBilled;
    this.billeting = scheduleItem.billeting;
    this.classifiedAccess = scheduleItem.classifiedAccess;
    this.restrictedAccess = scheduleItem.restrictedAccess;
    this.comsecaccess = scheduleItem.comsecaccess;
    this.teraccess = scheduleItem.teraccess;
    this.escortOnly = scheduleItem.escortOnly;
    this.restrictedAreaPhotography = scheduleItem.restrictedAreaPhotography;
    this.oanumber = scheduleItem.oanumber;
    this.copaId = scheduleItem.copaId;
    this.notes = scheduleItem.notes;
    this.createdOn = scheduleItem.createdOn;
    this.createdById = scheduleItem.createdById;
    this.scheduleItemStatusId = scheduleItem.scheduleItemStatusId;
    this.clearanceId = scheduleItem.clearanceId;
    this.reliefForJtId = scheduleItem.reliefForJtId;
    this.jtWsId = scheduleItem.empWsId;
    this.siteArrivalNoticeRequired = scheduleItem.siteArrivalNoticeRequired;
    this.schTravelRequestLegs = scheduleItem.schTravelRequestLegs;
    this.confirmArrival = scheduleItem.confirmArrival;
    this.confirmDeparture = scheduleItem.confirmDeparture;
    this.carrierId = scheduleItem.carrierId;
    this.flightNumber = scheduleItem.flightNumber;
    this.arrival = scheduleItem.arrival;
    this.departure = scheduleItem.departure;
    this.arrivalCarrierSchItemId = scheduleItem.arrivalCarrierSchItemId;
    this.departureCarrierSchItemId = scheduleItem.departureCarrierSchItemId;
    this.purpose = scheduleItem.purpose;
    this.firearmRequest = scheduleItem.firearmRequest;
    this.schFirearms = scheduleItem.schFirearms;
    this.radarDowntime = scheduleItem.radarDowntime;
    this.transToTopCamp = scheduleItem.transToTopCamp;
    this.modifiedById = scheduleItem.modifiedById;


    this.widthPercent = width + "%";

    let siType = ScheduleItemTypeEnum;
    let siStatus = ScheduleItemStatusEnum;

    if (this.color !== undefined) {
      let hexcolor = this.color.substr(1);
      const r = parseInt(hexcolor.substr(0, 2), 16);
      const g = parseInt(hexcolor.substr(2, 2), 16);
      const b = parseInt(hexcolor.substr(4, 2), 16);
      const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
      this.classes.push((yiq >= 128) ? 'blackText' : 'whiteText');
    }
    else {
      this.color = 'white';
    }

    if (this.siteName == '') {
      this.classes.push('ganttEmptyDay');
    }

    if (this.siteCode != '') {
      this.ganttDescription = ((this.scheduleItemStatusId == siStatus.pending) ? 'PENDING: ' + this.resourceDescription + ' for ' + this.name + ' ' : ((this.employeeId == null) ? this.resourceDescription + ' a ' + this.jobTitle + ' ' : this.jobTitle + ' ')) + ' @ ' + this.siteCode + ' ' + moment(this.start).format('M/D') + '-' + moment(this.end).format('M/D');
      this.hoverDescription = ((this.scheduleItemStatusId == siStatus.pending) ? 'PENDING: ' + this.resourceDescription + ' for ' + this.name + ' ' : ((this.employeeId == null) ? this.resourceDescription + ' a ' : '')) + this.jobTitle + ' @ ' + this.siteCode + ' ' + moment(this.start).format('M/D') + '-' + moment(this.end).format('M/D') + ' : ' + ((this.scheduleItemStatusId != siStatus.pending) ? ' ' + this.name : ' ') + ((this.scheduleItemTypeId == siType.training_emp || this.scheduleItemTypeId == siType.training_nonEmp) ? ' - ' + this.notes : '') + ((this.reliefForId != null) ? ' relief for - ' + this.reliefForName : '') + ((this.overhireJtId != null) ? ' overhire for - ' + this.overhireJt : '') + ((this.isVip) ? ' is a VIP' : '');
    }
    else {
      this.ganttDescription = null;
      this.hoverDescription = null;
    }

  }

  widthPercent: string;

  id: number;
  start: moment.Moment;
  end: moment.Moment;
  company: string;
  name: string;
  jobTitleId: null = null;
  jobTitle: string;
  siteId: number = null;
  siteName: string;
  siteCode: string;
  resourceId: number = null;
  employeeId: number = null;
  reliefForId: number = null;
  reliefForName: string;
  overhireJtId: number = null;
  overhireJt: string;
  resourceDescription: string;
  scheduleItemTypeId: number = null;
  requiresApproval: boolean = false;
  isEmployeeSiType: boolean = false;
  color: string;
  isVip: boolean = false;
  isCarrier: boolean = false;
  icon: string;
  gender: string;
  travelArrangeReq: boolean = false;
  meals: boolean = false;
  govBilled: boolean = false;
  companyBilled: boolean = false;
  billeting: boolean = false;
  classifiedAccess: boolean = false;
  restrictedAccess: boolean = false;
  comsecaccess: boolean = false;
  teraccess: boolean = false;
  escortOnly: boolean = false;
  restrictedAreaPhotography: boolean = false;
  oanumber: string;
  copaId: number;
  notes: string;
  createdOn: Date = null;
  createdById: number = null;
  scheduleItemStatusId: number = ScheduleItemStatusEnum.pending;
  confirmArrival: boolean = null;
  confirmDeparture: boolean = null;
  carrierId: number = null;
  flightNumber: string = null;
  arrival: Date = null;
  departure: Date = null;
  arrivalCarrierSchItemId: number = null;
  departureCarrierSchItemId: number = null;
  purpose: string = null;
  firearmRequest: boolean = false;
  schFirearms: Array<SchFirearms> = [];
  radarDowntime: boolean = false;
  transToTopCamp: boolean = false;

  classes: Array<string> = ['col-xs-11', 'ganttday'];
  ganttDescription: string;
  hoverDescription: string;
  clearanceId: number = null;
  reliefForJtId: number = null;
  jtWsId: number = null;
  siteArrivalNoticeRequired: boolean = false;
  schTravelRequestLegs: Array<SchTravelRequestLegs> = [];
  modifiedById: number = null;

}

